import * as React from 'react';
import './TitledContainer.scss';
import { Spinner, CustomInput, Tooltip, DropdownItem, ButtonDropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import Search from '@material-ui/icons/Search';
import { TextInputField, AutocompleteTextField } from '../Forms/fields';
import { ifEnter } from '../../shared/utils';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import ToolTip from '../ToolTip';
import AppButton from '../AppButton';

export interface TitleActionOptions {
    id: string,
    description: string,
    icon?: React.ReactNode,
    onClick: () => void,
}

export interface TitleAction {
    id: string,
    Icon: React.ElementType,
    options: TitleActionOptions[],
}

export interface TitledContainerProps {
    title: string;
    subTitle?: React.ReactNode;
    children: React.ReactNode;
    className?: string,
    isLoading?: boolean,
    searchOptions?: string[],
    searchOptionLabelResolver?: (option: string) => string,
    searchTextResolver?: (option: string) => string,
    searchMatcher?: (inputValue: string, option: string) => boolean,
    actions?: TitleAction[],
    extraActions?: React.ReactNode,
    titleMargin?: 'XS'|'S'|'M',
    id?: string,
    onSearch?: (keyword?: string) => void, // Adds search bar when defined
    onSearchOptionSelected?: (option: string) => void,
}

const TitledContainer : React.FC<TitledContainerProps> = ({
    title,
    subTitle,
    searchOptions,
    searchOptionLabelResolver,
    searchTextResolver,
    searchMatcher,
    children,
    className,
    isLoading,
    actions,
    extraActions,
    titleMargin = 'M',
    id,
    onSearch,
    onSearchOptionSelected,
}) => {

    const t = useTranslations();

    const [searchKeyword, setSearchKeyword] = React.useState<string|undefined>(undefined);
    const [searchKeywordTrimmed, setSearchKeywordTrimmed] = React.useState<string|undefined>(undefined)
    const [selectedSearchOption, setSelectedSearchOption] = React.useState<string | undefined>(undefined);
    const [freeTextTooltipOpen, setFreeTextTooltipOpen] = React.useState(false);
    const [actionsOpen, setActionsOpen] = React.useState(false);

    const _onSearch = React.useCallback((value?: string) => {
        if (!value) {
            onSearch && onSearch('');
        }
        const term = value?.trim().split(' ').filter(s => s.length).map(s => `"${s}"`).join(' ')
        if(term !== searchKeywordTrimmed) {
            setSearchKeywordTrimmed(term)
            onSearch && onSearch(term)
        }
    }, [onSearch, searchKeywordTrimmed]);

    const onSearchClick = React.useCallback(
        () => {
            if (selectedSearchOption && onSearchOptionSelected) {
                onSearchOptionSelected(selectedSearchOption);
                return;
            }

            _onSearch(searchKeyword)
        },
        [onSearchOptionSelected, searchKeyword, selectedSearchOption, _onSearch]);

    const handleSearchTextChanged = React.useCallback((value: string) => {
        setSearchKeyword(value);
        setSelectedSearchOption(undefined);
    }, []);

    const handleSearchValueChanged = React.useCallback((value: string) => {
        const isExistingOption = searchOptions?.includes(value) ?? false;
        const displayValue = searchOptionLabelResolver?.(value) ?? value;

        setSearchKeyword(displayValue);
        setSelectedSearchOption(isExistingOption ? value : undefined);

        if (isExistingOption && onSearchOptionSelected) {
            onSearchOptionSelected(value);
            return;
        }

        _onSearch(displayValue);
    }, [onSearchOptionSelected, searchOptionLabelResolver, searchOptions, _onSearch]);

    return (<>
        <div className="titledContainer" style={{marginBottom: `var(--spacer-${titleMargin})`}} id={id ? `${id}-container` : undefined}>
            <div className="actionBar">
                <h3>{title}</h3>
                { isLoading && <Spinner/> }
                <div className="actionBarButtons">
                    { onSearch && searchOptions &&
                        <div id="searchByNameContainer" >
                            <Tooltip
                                placement="bottom"
                                isOpen={freeTextTooltipOpen}
                                target="searchByNameContainer"
                                toggle={() => setFreeTextTooltipOpen(!freeTextTooltipOpen)}
                            >
                                {t(TK.freeTextSwitchMessage)}
                            </Tooltip>
                        </div>
                    }
                    { onSearch && !searchOptions &&
                        <TextInputField
                            id={id ? `${id}-search` : undefined}
                            className="searchInput"
                            value={searchKeyword}
                            onChange={setSearchKeyword}
                            onKeyUp={e => ifEnter(e, onSearchClick)}
                            type="search" />
                    }
                    { onSearch && searchOptions &&
                        <AutocompleteTextField
                            id={id ? `${id}-search` : undefined}
                            className="searchInput"
                            options={searchOptions}
                            optionLabelResolver={searchOptionLabelResolver}
                            searchTextResolver={searchTextResolver}
                            searchMatcher={searchMatcher}
                            value={searchKeyword}
                            onTextChanged={handleSearchTextChanged}
                            onValueChanged={handleSearchValueChanged}
                            onKeyDown={e => ifEnter(e, onSearchClick)}
                            noOptionsText={t(TK.pressEnterToSearch)} />
                    }
                    { onSearch &&
                        <AppButton id={id ? `${id}-search-button` : undefined} onClick={onSearchClick} disabled={isLoading}><Search/></AppButton>
                    }

                    { actions && actions.filter(action => action.options.some(o => o)).map((action) => action.options.length === 1
                        ?
                            <div key={action.id}>
                                <ToolTip target={`${id}-action-${action.id}-button`} toolTipContent={action.options[0].description}/>
                                <AppButton id={`${id}-action-${action.id}-button`} color="success" onClick={() => action.options[0].onClick()}><action.Icon/></AppButton>
                            </div>
                        :
                            <ButtonDropdown key={action.id} direction="left" isOpen={actionsOpen} toggle={() => setActionsOpen(!actionsOpen)}>
                                <div><DropdownToggle color="success"><action.Icon/></DropdownToggle></div>
                                <DropdownMenu>
                                    { action.options.map(option =>
                                        <DropdownItem
                                            id={id ? `${id}-action-${action.id}-${option.id}-option` : undefined}
                                            key={option.description}
                                            onClick={() => { option.onClick(); setActionsOpen(false); }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)' }}>
                                                {option.icon && <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', fontSize: '1rem' }}>{option.icon}</span>}
                                                {option.description}
                                            </span>
                                        </DropdownItem>) }
                                </DropdownMenu>
                            </ButtonDropdown>
                    )}

                    {extraActions}


                </div>
            </div>

            {subTitle}
        </div>

        <div className={className}>
            {children}
        </div>

    </>);
}

export default TitledContainer;
